import React, { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'

import { api } from '../lib/api'
import { Button, Card, ErrorMessage, Spinner } from './ui'

// The offer, and the only place the app sells anything.
//
// Rendered in two situations that differ only in their heading: after the free
// allowance runs out, and before it does — on /leagues, so someone who has
// decided early never has to exhaust a meter to pay. Everything below the
// heading is the same either way, because the compliance obligations do not
// change with the user's mood.
//
// Two things here are legal requirements rather than design choices, both from
// California's automatic renewal law, and both of which /terms already promises:
//
//   1. The renewal terms — that it renews, how often, for how much, and how to
//      cancel — are disclosed *before* purchase, on this page, next to the
//      button. That is why there is no Subscribe button at all when the price
//      cannot be read from Stripe: an unknown amount is not a lesser version of
//      this screen, it is a non-compliant one.
//   2. Consent to the automatic renewal is its own step — an unticked box the
//      user has to tick — not something bundled into pressing Subscribe. The
//      server refuses the checkout without it.
export default function Paywall({ entitlement }) {
  const [consented, setConsented] = useState(false)

  const price = useQuery({
    queryKey: ['billing', 'price'],
    queryFn: () => api.price(),
    retry: false,
    staleTime: 5 * 60 * 1000,
  })

  const checkout = useMutation({
    mutationFn: () => api.startCheckout(consented),
    onSuccess: (data) => {
      // A full navigation, not a router push: Stripe Checkout is their page.
      window.location.assign(data.checkout.url)
    },
  })

  const amount = price.data?.price?.formatted
  const used = entitlement?.free_used
  const limit = entitlement?.free_limit
  const remaining = entitlement?.free_remaining

  // Derived from the meter rather than passed in, because the caller should not
  // be able to get this wrong: this component was previously announcing that
  // the free evaluations were gone to anyone who had not subscribed, including
  // people who had all of them left.
  const metered = Number.isFinite(used) && Number.isFinite(limit)
  const exhausted = metered && remaining === 0

  return (
    <Card
      title={
        exhausted
          ? "You've used your free evaluations this season"
          : 'Subscribe for the season'
      }
      subtitle={
        exhausted
          ? `${used} of ${limit} used. Trades you've already evaluated stay readable — this is about evaluating new ones.`
          : metered
            ? `${remaining} of ${limit} free evaluations left — there is no need to wait until they run out.`
            : 'Unlimited evaluations for the season.'
      }
    >
      <ul className="space-y-1.5 text-sm text-slate-300">
        <li>Unlimited trade evaluations for the season</li>
        <li>Re-evaluate as injury news lands, at no extra cost</li>
        <li>Shareable links to any trade you evaluate</li>
      </ul>

      {price.isLoading && (
        <div className="mt-4">
          <Spinner label="Checking the price…" />
        </div>
      )}

      {price.error && (
        <p className="mt-4 rounded-md border border-amber-800 bg-amber-950/40 px-3 py-2 text-sm text-amber-200">
          Subscriptions are briefly unavailable — the price could not be confirmed just now.
          Nothing has been charged. Please try again shortly.
        </p>
      )}

      {amount && (
        <>
          {/* The pre-purchase disclosure. Deliberately adjacent to the button
              rather than behind a link, and deliberately stating the cancel
              route, because that is what "clear and conspicuous" means. */}
          <div className="mt-4 rounded-md border border-slate-600 bg-slate-950 p-3">
            <p className="text-sm font-semibold text-slate-100">
              {amount} per year, and it renews automatically each year until you cancel.
            </p>
            <p className="mt-1 text-xs text-slate-400">
              Renewals land in August, before the season starts — so your first term usually
              runs from today only until then, and you pay a prorated part of the price for
              it rather than a full year. Your exact first charge is shown at checkout before
              you pay; it is {amount} each year after that. We email you before every renewal.
              Cancel any time from your account page; you keep access until the term you paid
              for runs out.{' '}
              <a className="underline text-slate-300" href="/terms" target="_blank" rel="noreferrer">
                Terms
              </a>{' '}
              ·{' '}
              <a className="underline text-slate-300" href="/privacy" target="_blank" rel="noreferrer">
                Privacy
              </a>
            </p>
          </div>

          <label className="mt-3 flex cursor-pointer items-start gap-2 text-sm text-slate-200">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={consented}
              onChange={(event) => setConsented(event.target.checked)}
            />
            <span>
              I understand this subscription renews automatically at {amount} per year until I
              cancel it.
            </span>
          </label>

          <div className="mt-3">
            <Button
              onClick={() => checkout.mutate()}
              disabled={!consented || checkout.isPending}
            >
              {checkout.isPending ? 'Opening checkout…' : `Subscribe — ${amount}/year`}
            </Button>
          </div>
        </>
      )}

      <div className="mt-3">
        <ErrorMessage error={checkout.error} />
      </div>
    </Card>
  )
}
